import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";

export default class extends Controller {
  static targets = ["input", "list", "cardTemplate"];
  static values = {
    fieldName: String,          // "message[attachment]"
    append: { type: Boolean, default: true },
  };

  connect() {
    this.files = []; // Array<File>
    this.signedIds = []; // parallel to files; null while uploading or in non-eager mode

    this._onConfirm = (e) => this._handleConfirm(e);
    this._onCleared = (e) => this._handleCleared(e);

    this.element.addEventListener("file-upload-modal:confirm", this._onConfirm);
    this.element.addEventListener("file-upload-modal:cleared", this._onCleared);

    if (!this._isEager() && this.hasInputTarget && this.inputTarget.files?.length) {
      this.files = Array.from(this.inputTarget.files);
      this.signedIds = this.files.map(() => null);
      this._render();
    }
  }

  disconnect() {
    this.element.removeEventListener("file-upload-modal:confirm", this._onConfirm);
    this.element.removeEventListener("file-upload-modal:cleared", this._onCleared);
  }

  // --- Events from modal ---
  _handleConfirm(e) {
    const { files, fieldName } = e.detail || {};

    if (this.hasFieldNameValue && fieldName && fieldName !== this.fieldNameValue) return;

    const picked = Array.from(files || []);
    if (!picked.length) return;

    if (!this.appendValue) {
      if (this._isEager()) this._removeAllHiddenInputs();
      this.files = [];
      this.signedIds = [];
    }

    const previousLength = this.files.length;
    this.files = this._dedupe(this.files.concat(picked));

    while (this.signedIds.length < this.files.length) this.signedIds.push(null);
    this.signedIds.length = this.files.length;

    if (this._isEager()) {
      this._clearNativeInput();
      this.files.slice(previousLength).forEach((file) => this._eagerUpload(file));
    } else {
      this._syncInput();
    }
    this._render();
  }

  _handleCleared(e) {
    const { fieldName } = e.detail || {};
    if (this.hasFieldNameValue && fieldName && fieldName !== this.fieldNameValue) return;
    this.clear();
  }

  // --- Public API (for onclick in cards) ---
  remove(e) {
    const idx = Number(e.currentTarget?.dataset.index);
    if (Number.isNaN(idx)) return;
    const removedSignedId = this.signedIds[idx];
    this.files.splice(idx, 1);
    this.signedIds.splice(idx, 1);
    if (this._isEager()) {
      if (removedSignedId) this._removeHiddenInput(removedSignedId);
    } else {
      this._syncInput();
    }
    this._render();
  }

  clear() {
    this.files = [];
    this.signedIds = [];
    if (this._isEager()) {
      this._removeAllHiddenInputs();
    } else {
      this._syncInput();
    }
    this._render();
  }

  // --- Eager DirectUpload mode ---
  _isEager() {
    return Boolean(this.hasInputTarget && this.inputTarget.dataset.directUploadUrl);
  }

  _eagerUpload(file) {
    const url = this.inputTarget.dataset.directUploadUrl;
    const upload = new DirectUpload(file, url);
    upload.create((error, blob) => {
      const currentIdx = this.files.indexOf(file);
      if (currentIdx === -1) return; // user removed the card before upload finished

      if (error) {
        this.files.splice(currentIdx, 1);
        this.signedIds.splice(currentIdx, 1);
        this._announceUploadError(file);
        this._render();
        return;
      }

      this.signedIds[currentIdx] = blob.signed_id;
      this._appendHiddenInput(blob.signed_id);
    });
  }

  _appendHiddenInput(signedId) {
    const form = this._form();
    if (!form) return;
    const hidden = document.createElement("input");
    hidden.type = "hidden";
    hidden.name = this._eagerFieldName();
    hidden.value = signedId;
    hidden.dataset.eagerSignedId = signedId;
    form.appendChild(hidden);
  }

  _removeHiddenInput(signedId) {
    const form = this._form();
    if (!form) return;
    form
      .querySelectorAll(`input[type="hidden"][data-eager-signed-id="${signedId}"]`)
      .forEach((el) => el.remove());
  }

  _removeAllHiddenInputs() {
    const form = this._form();
    if (!form) return;
    form
      .querySelectorAll('input[type="hidden"][data-eager-signed-id]')
      .forEach((el) => el.remove());
  }

  _eagerFieldName() {
    if (this.hasFieldNameValue && this.fieldNameValue) return this.fieldNameValue;
    if (this.hasInputTarget) return this.inputTarget.getAttribute("name") || "";
    return "";
  }

  _announceUploadError(file) {
    window.dispatchEvent(
      new CustomEvent("notification:show", {
        detail: { type: "error", message: `Upload failed: ${file.name}. Please try again.` },
      })
    );
  }

  _form() {
    return this.element.closest("form") || this.element.querySelector("form");
  }

  _clearNativeInput() {
    if (!this.hasInputTarget) return;
    try {
      this.inputTarget.files = new DataTransfer().files;
    } catch (_err) {
      this.inputTarget.value = "";
    }
  }

  // --- Sync input & render ---
  _syncInput() {
    if (!this.hasInputTarget) return;

    try {
      const dt = new DataTransfer();
      this.files.forEach((f) => dt.items.add(f));
      this.inputTarget.files = dt.files;
    } catch (err) {
      // Fallback (old Safari):
      this.inputTarget.value = "";
    }
  }

  _render() {
    if (!this.hasListTarget) return;
    this.listTarget.innerHTML = "";
    if (!this.files.length) {
      this.listTarget.classList.remove("mt-12");
      return;
    }

    if (!this.hasCardTemplateTarget) {
      console.warn("[file-upload-host] Card template not found");
      return;
    }

    const frag = document.createDocumentFragment();
    this.listTarget.classList.add("mt-12");

    this.files.forEach((file, index) => {
      const card = this.cardTemplateTarget.content.cloneNode(true);

      const nameEl = card.querySelector("[data-file-name]");
      if (nameEl) nameEl.textContent = file.name;

      const extEl = card.querySelector("[data-file-ext]");
      if (extEl) extEl.textContent = this._ext(file.name).toUpperCase();

      const sizeEl = card.querySelector("[data-file-size]");
      if (sizeEl) sizeEl.textContent = this._size(file.size);

      const btn = card.querySelector("[data-file-index]");
      if (btn) {
        btn.dataset.index = String(index);
        btn.addEventListener("click", (ev) => this.remove(ev));
      }

      frag.appendChild(card);
    });

    this.listTarget.appendChild(frag);
  }

  // --- Utils ---
  _dedupe(arr) {
    const seen = new Set();
    const out = [];
    for (const f of arr) {
      const key = `${f.name}::${f.size}`;
      if (seen.has(key)) continue;
      seen.add(key); out.push(f);
    }
    return out;
  }

  _ext(n)  { const i = n.lastIndexOf("."); return i >= 0 ? n.slice(i+1) : ""; }
  _size(b) { if (!b) return "0 B"; const u=["B","KB","MB","GB","TB"]; const i=Math.floor(Math.log(b)/Math.log(1024)); return `${(b/1024**i).toFixed(i?2:0)} ${u[i]}`; }
}
